import React from 'react';
import { Badge } from '../components/ui/badge';
import { Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #0066FF 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #0066FF 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="absolute top-10 right-10 w-32 md:w-64 h-32 md:h-64 bg-[#0066FF]/20 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Badge className="bg-white/10 text-white hover:bg-white/20 border-0 mb-4">
            <Shield className="w-4 h-4 mr-2" />
            Legal
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-white">
            Privacy Policy
          </h1>
          <p className="text-gray-300">Last updated: December 2024</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto prose prose-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
          <p className="text-gray-600 mb-6">
            HRMQore ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our HR management platform and services.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
          <p className="text-gray-600 mb-4">We collect information that you provide directly to us, including:</p>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li>Personal identification information (name, email address, phone number)</li>
            <li>Employment information (job title, department, employee ID)</li>
            <li>Company information (organization name, industry, size)</li>
            <li>Payroll and financial data (salary information, bank details)</li>
            <li>Attendance and time tracking data</li>
            <li>Performance review and feedback data</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
          <p className="text-gray-600 mb-4">We use the information we collect to:</p>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li>Provide, maintain, and improve our services</li>
            <li>Process payroll and manage employee benefits</li>
            <li>Generate reports and analytics for HR management</li>
            <li>Communicate with you about updates and new features</li>
            <li>Ensure compliance with legal and regulatory requirements</li>
            <li>Protect against fraudulent or unauthorized activity</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
          <p className="text-gray-600 mb-6">
            We implement industry-standard security measures to protect your data, including encryption, secure data centers, access controls, and regular security audits. While we strive to protect your information, no method of transmission over the Internet is 100% secure.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
          <p className="text-gray-600 mb-6">
            We do not sell your personal information. We may share your data with trusted service providers who assist us in operating our platform, conducting our business, or servicing you, provided they agree to keep this information confidential.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights</h2>
          <p className="text-gray-600 mb-4">You have the right to:</p>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li>Access your personal data</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Object to processing of your data</li>
            <li>Export your data in a portable format</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Contact Us</h2>
          <p className="text-gray-600 mb-6">
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <div className="bg-gray-50 p-6 rounded-lg">
            <p className="text-gray-700 font-semibold">HRMQore</p>
            <p className="text-gray-600">Email: privacy@hrmqore.com</p>
            <p className="text-gray-600">Phone: 07042050336</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
